import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  FaCheck,
  FaCrown,
  FaGift,
  FaLock,
  FaShieldAlt,
  FaStar,
} from "react-icons/fa";

import {
  getCurrentSubscription,
  getSubscriptionPlans,
  selectSubscriptionPlan,
  createSubscriptionPaymentOrder,
  verifySubscriptionPayment,
  clearSelectPlanState,
  clearSubscriptionPaymentState,
} from "../../../../../store/tenantSubscription/subscriptionSlice";

import "./TenantSubscriptionPage.css";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const normalizeCode = (
  value
) => {

  return String(
    value || ""
  )
    .trim()
    .toUpperCase();

};


/*
|--------------------------------------------------------------------------
| CURRENCY
|--------------------------------------------------------------------------
*/

const formatCurrency = (
  value,
  currency = "INR"
) => {

  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",

      currency:
        currency ||
        "INR",

      maximumFractionDigits:
        0,
    }
  ).format(
    Number(
      value ||
      0
    )
  );

};


/*
|--------------------------------------------------------------------------
| PLAN LIMIT
|--------------------------------------------------------------------------
|
| Supports both:
|
| plan.maxEmployees
|
| and:
|
| plan.limits.maxEmployees
|
|--------------------------------------------------------------------------
*/

const getPlanLimit = (
  plan,
  key
) => {

  const value =
    plan?.[key] ??
    plan?.limits?.[key];


  /*
  |--------------------------------------------------------------------------
  | NULL = UNLIMITED
  |--------------------------------------------------------------------------
  */

  if (
    value === null
  ) {

    return "∞";

  }


  if (
    value === undefined ||
    value === ""
  ) {

    return "-";

  }


  return value;

};


/*
|--------------------------------------------------------------------------
| LOAD RAZORPAY
|--------------------------------------------------------------------------
*/

const loadRazorpayScript =
  () => {

    return new Promise(
      (
        resolve
      ) => {

        /*
        |--------------------------------------------------------------------------
        | ALREADY AVAILABLE
        |--------------------------------------------------------------------------
        */

        if (
          window.Razorpay
        ) {

          resolve(
            true
          );

          return;

        }


        /*
        |--------------------------------------------------------------------------
        | CHECK EXISTING SCRIPT
        |--------------------------------------------------------------------------
        */

        const existingScript =
          document.querySelector(
            'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
          );


        if (
          existingScript
        ) {

          existingScript.addEventListener(
            "load",
            () =>
              resolve(
                true
              ),
            {
              once: true,
            }
          );


          existingScript.addEventListener(
            "error",
            () =>
              resolve(
                false
              ),
            {
              once: true,
            }
          );


          return;

        }


        /*
        |--------------------------------------------------------------------------
        | CREATE SCRIPT
        |--------------------------------------------------------------------------
        */

        const script =
          document.createElement(
            "script"
          );


        script.src =
          "https://checkout.razorpay.com/v1/checkout.js";


        script.async =
          true;


        script.onload =
          () =>
            resolve(
              true
            );


        script.onerror =
          () =>
            resolve(
              false
            );


        document.body.appendChild(
          script
        );

      }
    );

  };


/*
|--------------------------------------------------------------------------
| TENANT SUBSCRIPTION PAGE
|--------------------------------------------------------------------------
*/

const TenantSubscriptionPage = ({
  embedded = false,

  onSubscriptionSuccess = null,
}) => {

  const dispatch =
    useDispatch();


  /*
  |--------------------------------------------------------------------------
  | REDUX
  |--------------------------------------------------------------------------
  */

  const {
    currentSubscription,
    plans,
    selectPlan,
    paymentOrder,
    verifyPayment,
  } =
    useSelector(
      (
        state
      ) =>
        state?.tenantSubscription ||
        {}
    );


  /*
  |--------------------------------------------------------------------------
  | CURRENT SUBSCRIPTION
  |--------------------------------------------------------------------------
  */

  const current =
    currentSubscription?.data ||
    null;


  /*
  |--------------------------------------------------------------------------
  | AVAILABLE PLANS
  |--------------------------------------------------------------------------
  */

  const availablePlans =
    Array.isArray(
      plans?.data
    )
      ?
      plans.data
      :
      [];


  /*
  |--------------------------------------------------------------------------
  | STATE
  |--------------------------------------------------------------------------
  */

  const [
    billingCycle,
    setBillingCycle,
  ] =
    useState(
      "MONTHLY"
    );


  const [
    processingPlanId,
    setProcessingPlanId,
  ] =
    useState(
      ""
    );


  const [
    pageError,
    setPageError,
  ] =
    useState(
      ""
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD DATA
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      dispatch(
        getCurrentSubscription()
      );


      dispatch(
        getSubscriptionPlans()
      );


      return () => {

        dispatch(
          clearSelectPlanState()
        );


        dispatch(
          clearSubscriptionPaymentState()
        );

      };

    },
    [
      dispatch,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | CURRENT PLAN CODE
  |--------------------------------------------------------------------------
  */

  const currentPlanCode =
    normalizeCode(

      current?.planCode ||

      current
        ?.planSnapshot
        ?.code ||

      current
        ?.planId
        ?.code

    );


  /*
  |--------------------------------------------------------------------------
  | SORT PLANS
  |--------------------------------------------------------------------------
  */

  const sortedPlans =
    useMemo(
      () => {

        return [
          ...availablePlans,
        ].sort(
          (
            a,
            b
          ) => {

            return (
              Number(
                a?.sortOrder ||
                0
              ) -
              Number(
                b?.sortOrder ||
                0
              )
            );

          }
        );

      },
      [
        availablePlans,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | CURRENT PLAN OBJECT
  |--------------------------------------------------------------------------
  */

  const currentPlan =
    useMemo(
      () => {

        if (
          !currentPlanCode
        ) {

          return null;

        }


        return (
          sortedPlans.find(
            (
              plan
            ) =>
              normalizeCode(
                plan?.code
              ) ===
              currentPlanCode
          ) ||
          null
        );

      },
      [
        sortedPlans,
        currentPlanCode,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | CURRENT PLAN ORDER
  |--------------------------------------------------------------------------
  */

  const currentPlanOrder =
    Number(
      currentPlan?.sortOrder ??
      -1
    );


  /*
  |--------------------------------------------------------------------------
  | PLAN ACCESS
  |--------------------------------------------------------------------------
  */

  const getPlanAccessState =
    (
      plan
    ) => {

      const planCode =
        normalizeCode(
          plan?.code
        );


      const planOrder =
        Number(
          plan?.sortOrder ??
          0
        );


      const isCurrent =
        Boolean(
          currentPlanCode
        ) &&
        planCode ===
          currentPlanCode;


      const isLowerPlan =
        Boolean(
          currentPlan
        ) &&
        planOrder <
          currentPlanOrder;


      return {

        isCurrent,

        isLowerPlan,

        disabled:
          isCurrent ||
          isLowerPlan,

      };

    };


  /*
  |--------------------------------------------------------------------------
  | PRICE
  |--------------------------------------------------------------------------
  */

  const getPrice =
    (
      plan
    ) => {

      if (
        billingCycle ===
        "YEARLY"
      ) {

        return Number(
          plan?.yearlyPrice ||
          0
        );

      }


      return Number(
        plan?.monthlyPrice ||
        0
      );

    };


  /*
  |--------------------------------------------------------------------------
  | YEARLY SAVING
  |--------------------------------------------------------------------------
  */

  const getYearlySaving =
    (
      plan
    ) => {

      const monthly =
        Number(
          plan?.monthlyPrice ||
          0
        );


      const yearly =
        Number(
          plan?.yearlyPrice ||
          0
        );


      if (
        monthly <=
          0 ||
        yearly <=
          0
      ) {

        return 0;

      }


      return Math.max(
        0,
        (
          monthly *
          12
        ) -
        yearly
      );

    };


  /*
  |--------------------------------------------------------------------------
  | MODULES
  |--------------------------------------------------------------------------
  */

  const getModules =
    (
      plan
    ) => {

      return (
        Array.isArray(
          plan?.modules
        )
          ?
          plan.modules
          :
          []
      ).filter(
        (
          module
        ) =>
          module?.enabled !==
          false
      );

    };


  /*
  |--------------------------------------------------------------------------
  | SUCCESS CALLBACK
  |--------------------------------------------------------------------------
  */

  const notifySubscriptionSuccess =
    () => {

      if (
        typeof onSubscriptionSuccess ===
        "function"
      ) {

        onSubscriptionSuccess();

      }

    };


  /*
  |--------------------------------------------------------------------------
  | SELECT PLAN
  |--------------------------------------------------------------------------
  */

  const handleSelectPlan =
    async (
      plan
    ) => {

      /*
      |--------------------------------------------------------------------------
      | PLAN TYPE
      |--------------------------------------------------------------------------
      */

      const planType =
        normalizeCode(
          plan?.planType
        );


      /*
      |--------------------------------------------------------------------------
      | CONTACT PLAN
      |--------------------------------------------------------------------------
      */

      if (
        planType ===
        "CONTACT"
      ) {

        return;

      }


      /*
      |--------------------------------------------------------------------------
      | PLAN ACCESS
      |--------------------------------------------------------------------------
      */

      const {
        disabled,
      } =
        getPlanAccessState(
          plan
        );


      if (
        disabled
      ) {

        return;

      }


      /*
      |--------------------------------------------------------------------------
      | PLAN ID
      |--------------------------------------------------------------------------
      */

      const planId =
        String(
          plan?._id ||
          ""
        );


      if (
        !planId
      ) {

        return;

      }


      try {

        /*
        |--------------------------------------------------------------------------
        | RESET ERROR
        |--------------------------------------------------------------------------
        */

        setPageError(
          ""
        );


        setProcessingPlanId(
          planId
        );


        /*
        |--------------------------------------------------------------------------
        | SELECT PLAN
        |--------------------------------------------------------------------------
        */

        const result =
          await dispatch(
            selectSubscriptionPlan({

              planId:
                plan._id,

              billingCycle,

            })
          )
            .unwrap();


        /*
        |--------------------------------------------------------------------------
        | SUBSCRIPTION
        |--------------------------------------------------------------------------
        */

        const subscription =
          result?.subscription ||
          result ||
          null;


        if (
          !subscription
        ) {

          throw new Error(
            "Unable to create subscription."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | PAYMENT NOT REQUIRED
        |--------------------------------------------------------------------------
        |
        | Example:
        |
        | Trial
        |
        |--------------------------------------------------------------------------
        */

        if (
          subscription?.paymentRequired ===
          false
        ) {

          await dispatch(
            getCurrentSubscription()
          );


          await dispatch(
            getSubscriptionPlans()
          );


          notifySubscriptionSuccess();


          return;

        }


        /*
        |--------------------------------------------------------------------------
        | CREATE PAYMENT ORDER
        |--------------------------------------------------------------------------
        */

        const paymentResult =
          await dispatch(
            createSubscriptionPaymentOrder({

              subscriptionId:
                subscription._id,

            })
          )
            .unwrap();


        /*
        |--------------------------------------------------------------------------
        | ALREADY PAID
        |--------------------------------------------------------------------------
        */

        if (
          paymentResult?.alreadyPaid ===
          true
        ) {

          await dispatch(
            getCurrentSubscription()
          );


          await dispatch(
            getSubscriptionPlans()
          );


          notifySubscriptionSuccess();


          return;

        }


        /*
        |--------------------------------------------------------------------------
        | RAZORPAY DATA
        |--------------------------------------------------------------------------
        |
        | Backend:
        |
        | {
        |   alreadyPaid: false,
        |
        |   subscription,
        |
        |   razorpay: {
        |       key,
        |       orderId,
        |       amount,
        |       currency
        |   }
        | }
        |
        |--------------------------------------------------------------------------
        */

        const razorpayData =
          paymentResult?.razorpay ||
          null;


        /*
        |--------------------------------------------------------------------------
        | VALIDATION
        |--------------------------------------------------------------------------
        */

        if (
          !razorpayData?.key
        ) {

          throw new Error(
            "Razorpay payment key was not returned by the server."
          );

        }


        if (
          !razorpayData?.orderId
        ) {

          throw new Error(
            "Razorpay order ID was not returned by the server."
          );

        }


        if (
          !razorpayData?.amount
        ) {

          throw new Error(
            "Razorpay order amount was not returned by the server."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | LOAD RAZORPAY
        |--------------------------------------------------------------------------
        */

        const loaded =
          await loadRazorpayScript();


        if (
          !loaded
        ) {

          throw new Error(
            "Unable to load Razorpay checkout."
          );

        }


        /*
        |--------------------------------------------------------------------------
        | OPEN RAZORPAY
        |--------------------------------------------------------------------------
        */

        const razorpay =
          new window.Razorpay({

            /*
            |--------------------------------------------------------------------------
            | RAZORPAY KEY
            |--------------------------------------------------------------------------
            */

            key:
              razorpayData.key,


            /*
            |--------------------------------------------------------------------------
            | ORDER
            |--------------------------------------------------------------------------
            */

            order_id:
              razorpayData.orderId,


            amount:
              razorpayData.amount,


            currency:
              razorpayData.currency ||
              plan?.currency ||
              "INR",


            /*
            |--------------------------------------------------------------------------
            | BUSINESS
            |--------------------------------------------------------------------------
            */

            name:
              "ComKwik",


            description:
              `${
                plan?.name ||
                "Subscription"
              } ${
                billingCycle ===
                "YEARLY"
                  ?
                  "Yearly"
                  :
                  "Monthly"
              } Plan`,


            /*
            |--------------------------------------------------------------------------
            | PAYMENT SUCCESS
            |--------------------------------------------------------------------------
            */

            handler:
              async (
                response
              ) => {

                try {

                  /*
                  |--------------------------------------------------------------------------
                  | VERIFY PAYMENT
                  |--------------------------------------------------------------------------
                  */

                  await dispatch(
                    verifySubscriptionPayment({

                      subscriptionId:
                        subscription._id,

                      razorpay_order_id:
                        response
                          ?.razorpay_order_id,

                      razorpay_payment_id:
                        response
                          ?.razorpay_payment_id,

                      razorpay_signature:
                        response
                          ?.razorpay_signature,

                    })
                  )
                    .unwrap();


                  /*
                  |--------------------------------------------------------------------------
                  | REFRESH SUBSCRIPTION
                  |--------------------------------------------------------------------------
                  */

                  await dispatch(
                    getCurrentSubscription()
                  );


                  /*
                  |--------------------------------------------------------------------------
                  | REFRESH PLANS
                  |--------------------------------------------------------------------------
                  */

                  await dispatch(
                    getSubscriptionPlans()
                  );


                  /*
                  |--------------------------------------------------------------------------
                  | CLOSE POPUP
                  |--------------------------------------------------------------------------
                  */

                  notifySubscriptionSuccess();

                } catch (
                  error
                ) {

                  console.error(
                    "Subscription payment verification failed:",
                    error
                  );


                  setPageError(
                    typeof error ===
                      "string"
                      ?
                      error
                      :
                      error?.message ||
                      "Payment verification failed."
                  );

                }

              },


            /*
            |--------------------------------------------------------------------------
            | THEME
            |--------------------------------------------------------------------------
            */

            theme: {
              color:
                "#8b5cf6",
            },

          });


        /*
        |--------------------------------------------------------------------------
        | PAYMENT FAILED
        |--------------------------------------------------------------------------
        */

        razorpay.on(
          "payment.failed",
          (
            response
          ) => {

            const message =

              response
                ?.error
                ?.description ||

              response
                ?.error
                ?.reason ||

              "Payment failed. Please try again.";


            setPageError(
              message
            );

          }
        );


        /*
        |--------------------------------------------------------------------------
        | OPEN
        |--------------------------------------------------------------------------
        */

        razorpay.open();

      } catch (
        error
      ) {

        console.error(
          "Subscription processing error:",
          error
        );


        setPageError(
          typeof error ===
            "string"
            ?
            error
            :
            error?.message ||
            "Unable to process subscription."
        );

      } finally {

        setProcessingPlanId(
          ""
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

const loading =
  embedded
    ?
    plans?.loading
    :
    currentSubscription?.loading ||
    plans?.loading;


  /*
  |--------------------------------------------------------------------------
  | PAGE
  |--------------------------------------------------------------------------
  */

  return (

    <div
      className={
        embedded
          ?
          "subscription-page subscription-page-embedded"
          :
          "subscription-page"
      }
    >

      <div className="subscription-container">


        {/* ================================================================
            HEADER
        ================================================================ */}

        <div className="subscription-header">

          <span className="subscription-eyebrow">

            <FaCrown />

            COMKWIK SUBSCRIPTION

          </span>


          <h1>
            Choose the right plan for your business
          </h1>


          <p>
            Unlock the modules, users and business tools you need.
            Choose yearly billing and get one month absolutely free.
          </p>

        </div>


        {/* ================================================================
            BILLING
        ================================================================ */}

        <div className="subscription-billing">

          <button
            type="button"

            className={
              billingCycle ===
              "MONTHLY"
                ?
                "active"
                :
                ""
            }

            onClick={
              () =>
                setBillingCycle(
                  "MONTHLY"
                )
            }
          >

            Monthly

          </button>


          <button
            type="button"

            className={
              billingCycle ===
              "YEARLY"
                ?
                "active yearly"
                :
                "yearly"
            }

            onClick={
              () =>
                setBillingCycle(
                  "YEARLY"
                )
            }
          >

            Yearly

            <span>
              1 MONTH FREE
            </span>

          </button>

        </div>


        {/* ================================================================
            YEARLY OFFER
        ================================================================ */}

        {
          billingCycle ===
          "YEARLY"
            ?
            (
              <div className="subscription-offer">

                <div className="subscription-offer-icon">

                  <FaGift />

                </div>


                <div>

                  <span>
                    YEARLY SPECIAL OFFER
                  </span>


                  <strong>
                    Pay for 11 months, enjoy 12 months
                  </strong>


                  <p>
                    Get one complete month absolutely free with yearly billing.
                  </p>

                </div>


                <div className="subscription-offer-badge">
                  1 MONTH FREE
                </div>

              </div>
            )
            :
            null
        }


        {/* ================================================================
            ERROR
        ================================================================ */}

        {
          pageError
            ?
            (
              <div className="subscription-error">

                {
                  pageError
                }

              </div>
            )
            :
            null
        }


        {/* ================================================================
            LOADING
        ================================================================ */}

        {
          loading
            ?
            (
              <div className="subscription-loading">

                Loading plans...

              </div>
            )
            :
            (
              /*
              |--------------------------------------------------------------------------
              | PLANS
              |--------------------------------------------------------------------------
              */

              <div className="subscription-plan-grid">

                {
                  sortedPlans.map(
                    (
                      plan
                    ) => {

                      /*
                      |--------------------------------------------------------------------------
                      | BASIC DETAILS
                      |--------------------------------------------------------------------------
                      */

                      const code =
                        normalizeCode(
                          plan?.code
                        );


                      const planType =
                        normalizeCode(
                          plan?.planType
                        );


                      const isGold =
                        code ===
                        "GOLD";


                      const isTrialPlan =
                        planType ===
                        "TRIAL";


                      const isContactPlan =
                        planType ===
                        "CONTACT";


                      /*
                      |--------------------------------------------------------------------------
                      | ACCESS
                      |--------------------------------------------------------------------------
                      */

                      const {
                        isCurrent,
                        isLowerPlan,
                        disabled,
                      } =
                        getPlanAccessState(
                          plan
                        );


                      /*
                      |--------------------------------------------------------------------------
                      | PRICE
                      |--------------------------------------------------------------------------
                      */

                      const price =
                        getPrice(
                          plan
                        );


                      /*
                      |--------------------------------------------------------------------------
                      | SAVING
                      |--------------------------------------------------------------------------
                      */

                      const saving =
                        getYearlySaving(
                          plan
                        );


                      /*
                      |--------------------------------------------------------------------------
                      | MODULES
                      |--------------------------------------------------------------------------
                      */

                      const modules =
                        getModules(
                          plan
                        );


                      /*
                      |--------------------------------------------------------------------------
                      | PROCESSING
                      |--------------------------------------------------------------------------
                      */

                      const isProcessing =
                        processingPlanId ===
                        String(
                          plan?._id ||
                          ""
                        );


                      /*
                      |--------------------------------------------------------------------------
                      | BUTTON TEXT
                      |--------------------------------------------------------------------------
                      */

                      let buttonText =
                        `Subscribe to ${
                          plan?.name ||
                          "Plan"
                        }`;


                      if (
                        isTrialPlan
                      ) {

                        buttonText =
                          "Start Free Trial";

                      }


                      if (
                        isContactPlan
                      ) {

                        buttonText =
                          "Contact Sales";

                      }


                      if (
                        isProcessing
                      ) {

                        buttonText =
                          "Processing...";

                      }


                      if (
                        isLowerPlan
                      ) {

                        buttonText =
                          "Included in Current Plan";

                      }


                      if (
                        isCurrent
                      ) {

                        buttonText =
                          "Current Plan";

                      }


                      /*
                      |--------------------------------------------------------------------------
                      | CARD
                      |--------------------------------------------------------------------------
                      */

                      return (

                        <article
                          key={
                            plan?._id ||
                            plan?.code
                          }

                          className={
                            `subscription-plan-card ${
                              isGold
                                ?
                                "gold"
                                :
                                ""
                            } ${
                              isCurrent
                                ?
                                "current"
                                :
                                ""
                            } ${
                              isLowerPlan
                                ?
                                "locked-plan"
                                :
                                ""
                            }`
                          }
                        >


                          {/* ==================================================
                              BADGES
                          ================================================== */}

                          <div className="subscription-card-badges">

                            {
                              isGold
                                ?
                                (
                                  <span className="subscription-popular">

                                    <FaStar />

                                    MOST POPULAR

                                  </span>
                                )
                                :
                                (
                                  <span />
                                )
                            }


                            {
                              isCurrent
                                ?
                                (
                                  <span className="subscription-current">

                                    CURRENT PLAN

                                  </span>
                                )
                                :
                                isLowerPlan
                                  ?
                                  (
                                    <span className="subscription-lower-plan">

                                      INCLUDED

                                    </span>
                                  )
                                  :
                                  null
                            }

                          </div>


                          {/* ==================================================
                              HEADING
                          ================================================== */}

                          <div className="subscription-plan-heading">

                            <div className="subscription-plan-icon">

                              {
                                isGold
                                  ?
                                  <FaCrown />
                                  :
                                  <FaShieldAlt />
                              }

                            </div>


                            <div>

                              <h2>

                                {
                                  plan?.name
                                }

                              </h2>


                              <p>

                                {
                                  plan?.description ||
                                  "Business management plan"
                                }

                              </p>

                            </div>

                          </div>


                          {/* ==================================================
                              PRICE
                          ================================================== */}

                          <div className="subscription-price">

                            <strong>

                              {
                                isContactPlan
                                  ?
                                  "Custom"

                                  :

                                  price >
                                  0
                                    ?
                                    formatCurrency(
                                      price,
                                      plan?.currency
                                    )
                                    :
                                    "FREE"
                              }

                            </strong>


                            {
                              !isContactPlan &&
                              price >
                              0
                                ?
                                (
                                  <span>

                                    {
                                      billingCycle ===
                                      "YEARLY"
                                        ?
                                        "/year"
                                        :
                                        "/month"
                                    }

                                  </span>
                                )
                                :
                                null
                            }

                          </div>


                          {/* ==================================================
                              YEARLY SAVING
                          ================================================== */}

                          {
                            !isContactPlan &&
                            billingCycle ===
                              "YEARLY" &&
                            saving >
                              0
                              ?
                              (
                                <div className="subscription-card-saving">

                                  <FaGift />


                                  <div>

                                    <strong>
                                      1 Month Absolutely Free
                                    </strong>


                                    <span>

                                      Save{" "}

                                      {
                                        formatCurrency(
                                          saving,
                                          plan?.currency
                                        )
                                      }

                                      {" "}
                                      every year

                                    </span>

                                  </div>

                                </div>
                              )
                              :
                              null
                          }


                          {/* ==================================================
                              LIMITS
                          ================================================== */}

                          <div className="subscription-card-limits">

                            <div className="subscription-limit-box">

                              <strong>

                                {
                                  getPlanLimit(
                                    plan,
                                    "maxEmployees"
                                  )
                                }

                              </strong>


                              <span>
                                Employees
                              </span>

                            </div>


                            <div className="subscription-limit-box">

                              <strong>

                                {
                                  getPlanLimit(
                                    plan,
                                    "maxBranches"
                                  )
                                }

                              </strong>


                              <span>
                                Branches
                              </span>

                            </div>


                            <div className="subscription-limit-box">

                              <strong>

                                {
                                  getPlanLimit(
                                    plan,
                                    "maxProductListings"
                                  )
                                }

                              </strong>


                              <span>
                                Product Listings
                              </span>

                            </div>

                          </div>


                          {/* ==================================================
                              MODULE HEADING
                          ================================================== */}

                          <div className="subscription-features-heading">

                            What's included

                            <span>

                              {
                                modules.length
                              } modules

                            </span>

                          </div>


                          {/* ==================================================
                              MODULES
                          ================================================== */}

                          <div className="subscription-module-list">

                            {
                              modules.length >
                              0
                                ?
                                modules.map(
                                  (
                                    module
                                  ) => (

                                    <div
                                      key={
                                        module?.code ||
                                        module?.name
                                      }
                                    >

                                      <span className="subscription-check">

                                        <FaCheck />

                                      </span>


                                      <span>

                                        {
                                          module?.name ||
                                          module?.code
                                        }

                                      </span>

                                    </div>

                                  )
                                )
                                :
                                (
                                  <div className="subscription-no-modules">

                                    <span>
                                      Contact us for available modules
                                    </span>

                                  </div>
                                )
                            }

                          </div>


                          {/* ==================================================
                              BUTTON
                          ================================================== */}

                          <button
                            type="button"

                            className={
                              `subscription-plan-button ${
                                isGold
                                  ?
                                  "gold"
                                  :
                                  ""
                              } ${
                                disabled ||
                                isContactPlan
                                  ?
                                  "disabled-plan-button"
                                  :
                                  ""
                              }`
                            }

                            disabled={
                              disabled ||
                              isContactPlan ||
                              isProcessing ||
                              selectPlan?.loading ||
                              paymentOrder?.loading ||
                              verifyPayment?.loading
                            }

                            onClick={
                              () =>
                                handleSelectPlan(
                                  plan
                                )
                            }
                          >

                            {
                              buttonText
                            }

                          </button>

                        </article>

                      );

                    }
                  )
                }

              </div>
            )
        }


        {/* ================================================================
            SECURITY
        ================================================================ */}

        <div className="subscription-security">

          <FaLock />

          Secure payments powered by Razorpay

        </div>

      </div>

    </div>

  );

};


export default TenantSubscriptionPage;